/**
 * Making the app use the tunnel without rewriting the app.
 *
 * The app talks to its backend in two ways and nowhere else: axios, in a
 * hundred and twenty places across twenty files, and fetch, in seven places
 * across three. Neither is worth editing by hand, and editing them by hand is
 * how half of them end up still pointing at a server that is not there.
 *
 * Axios lets its transport be replaced with one function. Fetch is replaced
 * on the window with one that answers api calls through the tunnel and hands
 * everything else to the real one, because the page itself still comes from
 * somewhere and still has to be able to load.
 *
 * Nothing here knows about WebRTC. It is handed something that can carry a
 * request and bring back an answer, which is exactly what a test can be too.
 */

import axios from "axios";
import { API_ORIGIN } from "./api";

/** The part of an address the tunnel needs: a path, with its query. */
export function pathOf(url = "", { baseURL = "", params } = {}) {
  let whole = String(url);
  if (baseURL && !/^https?:\/\//i.test(whole)) {
    whole = `${String(baseURL).replace(/\/+$/, "")}/${whole.replace(/^\/+/, "")}`;
  }
  if (/^https?:\/\//i.test(whole)) {
    const parsed = new URL(whole);
    whole = `${parsed.pathname}${parsed.search}`;
  }
  const query = params ? new URLSearchParams(params).toString() : "";
  if (!query) return whole;
  return `${whole}${whole.includes("?") ? "&" : "?"}${query}`;
}

/** Whether this is a call the tunnel is meant to carry. */
export function isAppCall(url, options = {}) {
  const path = pathOf(url, options);
  return path.startsWith("/api");
}

/**
 * Axios, but the request crosses the channel.
 *
 * Rejecting on a bad status is the adapter's job once the adapter is ours;
 * without it every 401 in the app would look like a success with odd data.
 */
export function tunnelAdapter(client) {
  return async function carry(config) {
    const answer = await client.request({
      method: (config.method || "get").toUpperCase(),
      path: pathOf(config.url, config),
      headers: { ...(config.headers || {}) },
      body: bodyOf(config.data),
    });

    const text = await answer.text();
    const response = {
      data: parse(text, answer.headers["content-type"]),
      status: answer.status,
      statusText: "",
      headers: answer.headers,
      config,
      request: null,
    };

    const acceptable = config.validateStatus
      ? config.validateStatus(answer.status)
      : answer.status >= 200 && answer.status < 300;
    if (acceptable) return response;

    const failure = new Error(`Request failed with status code ${answer.status}`);
    failure.isAxiosError = true;
    failure.response = response;
    failure.config = config;
    throw failure;
  };
}

/**
 * Fetch, but api calls cross the channel.
 *
 * What comes back is shaped like a Response in the ways this app uses one:
 * ok, status, headers.get, json, text, and a body that can be read piece by
 * piece. That last one is the council screen and it is the reason this is not
 * simply "await the whole thing and hand it over".
 */
export function tunnelFetch(client, realFetch) {
  return async function carry(url, options = {}) {
    if (!isAppCall(url, options)) return realFetch(url, options);

    const answer = await client.request({
      method: (options.method || "GET").toUpperCase(),
      path: pathOf(url, options),
      headers: { ...(options.headers || {}) },
      body: bodyOf(options.body),
    });

    return {
      ok: answer.status >= 200 && answer.status < 300,
      status: answer.status,
      headers: { get: (name) => answer.headers[String(name).toLowerCase()] ?? null },
      text: () => answer.text(),
      json: async () => JSON.parse(await answer.text()),
      body: { getReader: () => reader(answer) },
    };
  };
}

/**
 * Turns the app's calls onto the tunnel, and gives back the way to undo it.
 *
 * Undoing matters: the desktop side of a connection is the same app, and it
 * must keep talking to its own backend directly. Only the phone is on the
 * far end of a channel.
 */
export function sendAppCallsThrough(client, { axiosInstance = axios, target = globalThis } = {}) {
  const previousAdapter = axiosInstance.defaults.adapter;
  const previousFetch = target.fetch;

  axiosInstance.defaults.adapter = tunnelAdapter(client);
  if (previousFetch) target.fetch = tunnelFetch(client, previousFetch.bind(target));

  return function stopUsingTunnel() {
    axiosInstance.defaults.adapter = previousAdapter;
    if (previousFetch) target.fetch = previousFetch;
  };
}

/** Where this app's own backend is, for the desktop half. */
export const OWN_BACKEND = API_ORIGIN;

function bodyOf(data) {
  if (data == null) return null;
  if (typeof data === "string") return data;
  return JSON.stringify(data);
}

function parse(text, contentType = "") {
  if (!text) return "";
  if (!String(contentType).includes("json")) return text;
  try {
    return JSON.parse(text);
  } catch {
    // A backend that says json and sends something else is the backend's
    // problem, not a reason to lose the answer.
    return text;
  }
}

/** A reader over pieces as they arrive, in the shape the app already reads. */
function reader(answer) {
  const queue = [];
  let waiting = null;
  let finished = false;

  answer.onChunk((piece) => {
    if (waiting) {
      const hand = waiting;
      waiting = null;
      hand({ done: false, value: new TextEncoder().encode(piece) });
    } else {
      queue.push(piece);
    }
  });
  answer.text().then(
    () => {
      finished = true;
      if (waiting) {
        const hand = waiting;
        waiting = null;
        hand({ done: true });
      }
    },
    () => {
      finished = true;
      if (waiting) {
        const hand = waiting;
        waiting = null;
        hand({ done: true });
      }
    }
  );

  return {
    read() {
      if (queue.length) {
        return Promise.resolve({ done: false, value: new TextEncoder().encode(queue.shift()) });
      }
      if (finished) return Promise.resolve({ done: true });
      return new Promise((resolve) => {
        waiting = resolve;
      });
    },
  };
}
